import { SEKCJE_PANELU, type SekcjaPanelu } from '@/lib/sekcjePanelu';

/**
 * RZĄD FILTRÓW — model kolejności (propozycja Maćka, zatwierdzona przez Piotra 6.10).
 *
 * Kolejność jest STAŁA, żeby ten sam filtr zawsze stał w tym samym miejscu:
 *  1. szybkie filtry Cena, Pokoje, Metraż — zawsze, na telefonie i pulpicie (wyłączone jako
 *     rozwijane pastylki; plan E2.1 (1) — do A8 telefon chował wyłączony Metraż);
 *  2. sugestie Metro i Ze zwierzęciem — zawsze, w tym samym miejscu włączone i wyłączone;
 *  3. pozostałe aktywne filtry w kolejności panelu (tak, jak przychodzą).
 * Rysuje `RzadFiltrow` (src/components/search/RzadFiltrow.tsx), składa SearchPage.
 */

/**
 * Sekcje panelu filtrów — dotknięcie chipa otwiera panel na nich. Od A8 to wiersze panelu A2
 * (`SEKCJE_PANELU`, `data-sekcja` w SearchFilters), nie dawne sekcje płaskiego panelu.
 */
export type SekcjaFiltrow = SekcjaPanelu;

export type IdSzybkiego = 'cena' | 'pokoje' | 'metraz';
export type IdSugestii = 'metro' | 'zwierzeta';
export type IdStalegoFiltra = IdSzybkiego | IdSugestii;

export const SZYBKIE: readonly IdSzybkiego[] = ['cena', 'pokoje', 'metraz'];
/** Dwie sugestie przy braku filtrów (propozycja Maćka): najchętniej widziane filtry. */
export const SUGESTIE: readonly IdSugestii[] = ['metro', 'zwierzeta'];

export type MiejsceWRzedzie =
  | { typ: 'szybki'; id: IdSzybkiego; aktywny: boolean }
  | { typ: 'sugestia'; id: IdSugestii; aktywny: boolean }
  | { typ: 'inny'; indeks: number };

/**
 * Kolejność elementów rzędu. `aktywne` — aktywne filtry w kolejności panelu, każdy z `id`, jeśli
 * ma stałe miejsce; `indeks` w wyniku wskazuje pozycję w tej tablicy.
 */
export function ukladRzedu(aktywne: ReadonlyArray<{ id?: IdStalegoFiltra }>, kompakt: boolean): MiejsceWRzedzie[] {
  const wlaczone = new Set(aktywne.map((a) => a.id).filter(Boolean));
  const wynik: MiejsceWRzedzie[] = [];
  for (const id of SZYBKIE) {
    wynik.push({ typ: 'szybki', id, aktywny: wlaczone.has(id) });
  }
  for (const id of SUGESTIE) wynik.push({ typ: 'sugestia', id, aktywny: wlaczone.has(id) });
  aktywne.forEach((a, indeks) => {
    if (!a.id) wynik.push({ typ: 'inny', indeks });
  });
  return wynik;
}

/** Klucz elementu rzędu — `data-chip`, po nim rząd poznaje nowo włączony filtr. */
export function kluczMiejsca(m: MiejsceWRzedzie, etykieta?: string): string {
  if (m.typ === 'inny') return `inny:${etykieta ?? m.indeks}`;
  return `${m.typ}:${m.id}`;
}

/**
 * Które elementy są NOWE i AKTYWNE względem poprzedniego zestawu — do nich rząd się przewija
 * (włączenie z panelu nie zostawia człowieka na początku rzędu, szukającego, gdzie filtr poszedł).
 * Przy pierwszym renderze (`poprzednie = null`) niczego nie przewijamy.
 */
export function noweAktywne(poprzednie: ReadonlySet<string> | null, obecne: ReadonlyArray<{ klucz: string; aktywny: boolean }>): string[] {
  if (!poprzednie) return [];
  return obecne.filter((e) => e.aktywny && !poprzednie.has(e.klucz)).map((e) => e.klucz);
}

/**
 * „Pozostałe aktywne filtry w KOLEJNOŚCI PANELU” (plan E2.1 (1)): stabilne sortowanie po sekcji
 * (`SEKCJE_PANELU`); w obrębie sekcji zostaje kolejność, w jakiej strona je dokłada (ta sama co
 * kontrolki w wierszu). Chip bez sekcji (bez adresu, narysowany obszar) idzie na koniec. Do
 * audytu 16.10 Kaucja stała przed Ogłoszeniem, a Właściciel i Ulubione na samym końcu.
 */
export function wKolejnosciPanelu<T extends { sekcja?: SekcjaFiltrow }>(chipy: readonly T[]): T[] {
  const miejsce = (c: T) => (c.sekcja ? SEKCJE_PANELU.indexOf(c.sekcja) : SEKCJE_PANELU.length);
  return chipy
    .map((c, i) => ({ c, i }))
    .sort((a, b) => miejsce(a.c) - miejsce(b.c) || a.i - b.i)
    .map(({ c }) => c);
}
